import React from "react";
import { Box, useTheme } from "@mui/material";
import { observer } from "mobx-react-lite";
import { SwagstoreSettingsModel } from "../../../models/Swagstore/SwagstoreSettingsModel";
import { Customer } from "../../../models/Customer/Customer";
import StoreSettings from "./settings/StoreSettings";
import LoginPageSettings from "./settings/LoginPageSettings";
import BrandingSettings from "./settings/BrandingSettings";
import ProductSettings from "./settings/ProductSettings";
import MockupsSettingsPanel from "./settings/MockupsSettingsPanel";
import { SelectedItem } from "../../../stores/SwagStoreStore";
import { SWAGSTORE_STEPS } from "../../../constants/SwagstoreConstants";

interface RightSidebarProps {
  selectedItem: SelectedItem;
  settings: SwagstoreSettingsModel;
  customers?: Customer[];
  storeDetailsErrors?: Record<string, string>;
  onSettingChange: (step: string, key: string, value: any) => void;
  onSaveSettings: () => void;
  onProductPriceChange: (productId: number, price: number | null) => void;
  onBulkPricingApply: () => void;
  onAddProducts: () => void;
  onDownloadProductsPacksCSV: () => void;
  onUploadProductsPacksCSV: (file: File) => void;
  onSearchTermChange: (term: string) => void;
  onCategoryChange: (category: string) => void;
  onProductIdChange: (id: number | null) => void;
  onBulkUpdateToggle: (enabled: boolean) => void;
  onMarkupPercentageChange: (percentage: number) => void;
  onDiscountUpdateToggle: (enabled: boolean) => void;
  onDiscountPercentageChange: (percentage: number) => void;
  onBulkDiscountApply: () => void;
  onResetPricing: () => void;
  onModalOpenChange: (open: boolean) => void;
}

const RightSidebar: React.FC<RightSidebarProps> = observer(
  ({
    selectedItem,
    settings,
    customers = [],
    storeDetailsErrors = {},
    onSettingChange,
    onSaveSettings,
    onProductPriceChange,
    onBulkPricingApply,
    onAddProducts,
    onDownloadProductsPacksCSV,
    onUploadProductsPacksCSV,
    onSearchTermChange,
    onCategoryChange,
    onProductIdChange,
    onBulkUpdateToggle,
    onMarkupPercentageChange,
    onDiscountUpdateToggle,
    onDiscountPercentageChange,
    onBulkDiscountApply,
    onResetPricing,
    onModalOpenChange,
  }) => {
    const theme = useTheme();

    return (
      <Box
        sx={{
          width: "350px",
          height: "100vh",
          backgroundColor: theme.palette.background.paper,
          borderLeft: `1px solid ${theme.palette.divider}`,
          overflowY: "auto",
        }}
      >
        <Box sx={{ p: 3 }}>
          {selectedItem === SWAGSTORE_STEPS.STORE_DETAILS ? (
            <StoreSettings
              settings={settings.storeDetails}
              customers={customers}
              isLoading={false}
              errors={storeDetailsErrors}
              onSettingChange={(key, value) =>
                onSettingChange(SWAGSTORE_STEPS.STORE_DETAILS, key, value)
              }
              onSaveChanges={onSaveSettings}
            />
          ) : selectedItem === SWAGSTORE_STEPS.LOGIN ? (
            <LoginPageSettings
              onSettingChange={(key, value) =>
                onSettingChange(SWAGSTORE_STEPS.LOGIN, key, value)
              }
              onSaveChanges={onSaveSettings}
            />
          ) : selectedItem === SWAGSTORE_STEPS.MOCKUPS ? (
            <MockupsSettingsPanel />
          ) : selectedItem === SWAGSTORE_STEPS.PRODUCT_MAPPING ? (
            <ProductSettings
              onSettingChange={(key, value) =>
                onSettingChange(SWAGSTORE_STEPS.PRODUCT_MAPPING, key, value)
              }
              onSaveChanges={onSaveSettings}
              onSearchTermChange={onSearchTermChange}
              onCategoryChange={onCategoryChange}
              onProductIdChange={onProductIdChange}
              onBulkUpdateToggle={onBulkUpdateToggle}
              onMarkupPercentageChange={onMarkupPercentageChange}
              onDiscountUpdateToggle={onDiscountUpdateToggle}
              onDiscountPercentageChange={onDiscountPercentageChange}
              onBulkDiscountApply={onBulkDiscountApply}
              onResetPricing={onResetPricing}
              onModalOpenChange={onModalOpenChange}
              onPriceChange={onProductPriceChange}
              onBulkPricingApply={onBulkPricingApply}
              onAddProducts={onAddProducts}
              onDownloadProductsPacksCSV={onDownloadProductsPacksCSV}
              onUploadProductsPacksCSV={onUploadProductsPacksCSV}
            />
          ) : (
            <BrandingSettings
              settings={settings.branding}
              onSettingChange={(key, value) =>
                onSettingChange(SWAGSTORE_STEPS.BRANDING, key, value)
              }
              onSaveSettings={onSaveSettings}
            />
          )}
        </Box>
      </Box>
    );
  }
);

export default RightSidebar;
